import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type RefObject,
} from "react"

import type { Region } from "wavesurfer.js/dist/plugins/regions.js"

interface RegionSelectionPlugin {
  getRegions: () => Region[]
}

/** Where the selection menu anchors: the pointer, or the segment for a key press. */
interface RegionSelectionPoint {
  clientX: number
  clientY: number
}

interface UseAudioRegionSelectionOptions {
  enabled: boolean
  regionsPluginRef: RefObject<RegionSelectionPlugin | null>
}

export function useAudioRegionSelection({
  enabled,
  regionsPluginRef,
}: UseAudioRegionSelectionOptions) {
  const [selectedRegionId, setSelectedRegionId] = useState<string | null>(null)
  const [selectedPosition, setSelectedPosition] = useState<{
    x: number
    y: number
  } | null>(null)
  const [clampedPosition, setClampedPosition] = useState<{
    left: number
    top: number
  } | null>(null)
  const [openedFromKeyboard, setOpenedFromKeyboard] = useState(false)
  const regionClickJustFiredRef = useRef(false)
  const menuRef = useRef<HTMLDivElement | null>(null)
  const clickOutsideTimerRef = useRef<number | null>(null)

  const clearSelection = useCallback(() => {
    setSelectedRegionId(null)
    setSelectedPosition(null)
  }, [])

  const handleRegionClicked = useCallback(
    (
      regionId: string,
      point: RegionSelectionPoint,
      { fromKeyboard = false }: { fromKeyboard?: boolean } = {},
    ) => {
      regionClickJustFiredRef.current = !fromKeyboard
      setOpenedFromKeyboard(fromKeyboard)
      setSelectedRegionId(regionId)
      setSelectedPosition({ x: point.clientX, y: point.clientY })
    },
    [],
  )

  /** Closes the menu and puts focus back on the segment it was editing. */
  const dismissSelection = useCallback(() => {
    const element = regionsPluginRef.current
      ?.getRegions()
      .find((region) => region.id === selectedRegionId)?.element
    clearSelection()
    element?.focus()
  }, [clearSelection, regionsPluginRef, selectedRegionId])

  const deleteSelectedRegion = useCallback(() => {
    const plugin = regionsPluginRef.current
    if (!plugin || !selectedRegionId || !enabled) {
      return
    }
    plugin
      .getRegions()
      .find((region) => region.id === selectedRegionId)
      ?.remove()
    clearSelection()
  }, [clearSelection, enabled, regionsPluginRef, selectedRegionId])

  useLayoutEffect(() => {
    if (!selectedPosition) {
      setClampedPosition(null)
      return
    }
    const clamp = () => {
      if (!menuRef.current) {
        return
      }
      const margin = 8
      const { width, height } = menuRef.current.getBoundingClientRect()
      setClampedPosition({
        left: Math.max(
          margin,
          Math.min(selectedPosition.x + 10, window.innerWidth - width - margin),
        ),
        top: Math.max(
          margin,
          Math.min(
            selectedPosition.y + 10,
            window.innerHeight - height - margin,
          ),
        ),
      })
    }
    clamp()
    window.addEventListener("resize", clamp)
    return () => window.removeEventListener("resize", clamp)
  }, [selectedPosition])

  useEffect(() => {
    if (!selectedRegionId || !enabled) {
      return
    }
    const handleDocumentClick = (event: MouseEvent) => {
      const target = event.target as Node
      if (menuRef.current?.contains(target)) {
        return
      }
      regionClickJustFiredRef.current = false
      if (clickOutsideTimerRef.current) {
        window.clearTimeout(clickOutsideTimerRef.current)
      }
      clickOutsideTimerRef.current = window.setTimeout(() => {
        clickOutsideTimerRef.current = null
        if (regionClickJustFiredRef.current) {
          regionClickJustFiredRef.current = false
          return
        }
        clearSelection()
      }, 0)
    }
    document.addEventListener("click", handleDocumentClick, true)
    return () => {
      document.removeEventListener("click", handleDocumentClick, true)
      if (clickOutsideTimerRef.current) {
        window.clearTimeout(clickOutsideTimerRef.current)
        clickOutsideTimerRef.current = null
      }
    }
  }, [clearSelection, enabled, selectedRegionId])

  return {
    clampedPosition,
    clearSelection,
    deleteSelectedRegion,
    dismissSelection,
    handleRegionClicked,
    menuRef,
    openedFromKeyboard,
    selectedPosition,
    selectedRegionId,
  }
}
